import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppState } from '../app.state';
import { catchError, concatMap, switchMap, withLatestFrom, take, filter, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { createPreloadedSearchableItems } from 'src/app/shared/data-flattener-pages';
import { FlattenEventsWorkerService } from 'src/app/development/flatten-events.worker.service';
import { of, forkJoin } from 'rxjs';
import { SearchableActions } from '@states/searchable/searchable.action-types';
import { FlattenEventsWorkerModel } from '@models/flatten-events-worker.model';
import { EntityLookupSelectors } from '@states/entity-lookup/entity-lookup.selector-types';
import { FlattenWallsWorkerService } from 'src/app/development/flatten-walls.worker.service';
import { FlattenWallsWorkerModel } from '@models/flatten-walls-worked.model';
// import { DataFlattenerWorkerService } from 'src/app/development/data-flattener.worker.service-orig';
import { DataFlattenerWorkerService } from 'src/app/development/data-flattener.worker.service';
import { DataFlattenerWorkerModel } from '@models/data-flattener-worker.model';
import {
  flattenAlarmData,
  flattenArticleData,
  flattenEventData,
  flattenFloorData,
  flattenLocationData,
  flattenPeopleData,
  flattenWallData,
} from 'src/app/shared/data-flattener';
import { SearchableSelectors } from '@states/searchable/searchable.selector-types';
import { AlarmService } from 'src/app/services/alarm.service';
import { HttpErrorResponse } from '@angular/common/module.d-CnjH8Dlt';
import { AlertEventsService } from 'src/app/development/alert-events.service';
import { EventModels } from '@models/alerts-v2.model';
import { PeopleService } from 'src/app/development/people.service';
import { WallV2Actions } from '@states/wall-v2/wall-v2.action-types';
import { WallV2Service } from 'src/app/services/wall-v2.service';
import { FloorService } from 'src/app/development/floor.service';
import { ArticlesService } from 'src/app/shared/services/articles.service';
import { LocationsService } from 'src/app/locations/locations.service';
import { HomeService } from 'src/app/development/home.service';
import { HomeModel } from '@models/home.model';
import { LocationModel } from 'src/app/locations/location.model';
import { Edge } from 'src/app/edge/edge.model';

const SEARCHABLE_SYNC_INITIAL_PROGRESS = 2;
const SEARCHABLE_SYNC_REQUEST_PROGRESS = 1;

@Injectable()
export class SearchableEffects {

  searchableSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.SearchableSync),
      concatMap(() =>
        forkJoin({
          locations: this.locationService.getGlobalSearchLocationsData(),
          homeLocations: this.homeService.getHomeLocations(),
        }).pipe(
          switchMap(({ locations, homeLocations }) => {
            const cameraEntities = this.buildCameraEntities(locations);
            const normalizedHome = this.normalizeHomeLocations(homeLocations, cameraEntities);

            console.log(`[SEARCHABLE-REFRESH] Data fetched: ${locations?.length || 0} locations, ${homeLocations?.length || 0} homes`);

            return [
              SearchableActions.FlattenSearchableLocations({
                locations: locations || [],
                home: normalizedHome,
              }),
              SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_INITIAL_PROGRESS }),
            ];
          }),
          catchError(err => {
            console.error('[SEARCHABLE-REFRESH]   Sync failed:', err?.message || err);
            return of(
              SearchableActions.FlattenSearchableFail({ error: err }),
              SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_INITIAL_PROGRESS }),
            );
          }),
        ),
      ),
    ),
  );

  flattenLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.FlattenSearchableLocations),
      concatMap(({ locations, home }) =>
        this.dataFlattenerWorkerService.waitForWorkerReady().pipe(
          switchMap(({ reason }) => {
            console.log(`[EFFECT-LOCATIONS]: 🚀 Starting location flattening via ${reason === 'worker_ready' ? 'WORKER READY' : 'TIMEOUT FALLBACK'} at ${new Date().toISOString()}`);

            return this.dataFlattenerWorkerService.runFlattenWorker({ dataType: 'location', locations, home }, () => {
              const fallbackResult = flattenLocationData(locations, home);
              console.log(`[EFFECT-LOCATIONS]: Using fallback flattening for locations at ${new Date().toISOString()}`, fallbackResult);
              return {
                type: 'RUN_FLATTEN_SUCCESS',
                requestId: 'fallback',
                searchableItems: [
                  ...fallbackResult?.searchableLocations,
                  ...fallbackResult?.searchableEdges,
                  ...fallbackResult?.searchableCameras,
                  ...fallbackResult?.searchableSubLocations,
                  ...fallbackResult?.searchableSpeakers,
                ],
              };
            });
          }),
        ).pipe(
          switchMap((result: DataFlattenerWorkerModel.RunFlattenSuccessMessage) => {
            console.log(`[EFFECT-LOCATIONS]: Completed location flattening, found ${result.searchableItems.length} items at ${new Date().toISOString()}`);
            return [
              SearchableActions.SetSearchableItems({
                payload: [
                  ...result?.searchableItems,
                ],
                loadedType: 'location',
              }),
            ];
          }),
          catchError(err => {
            console.error(`[EFFECT-LOCATIONS]: Location flattening failed at ${new Date().toISOString()}`, err);
            return of(SearchableActions.FlattenSearchableFail({ error: err }));
          }),
        ),
      ),
    ),
  );

  initializePreloadedItemsOnFirstLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.SetSearchableItems),
      filter(({ loadedType }) => loadedType === 'location'),
      take(1),
      switchMap(() => {
        const translatedItems = createPreloadedSearchableItems(
          (key: string) => this.translateService.instant(key),
        );
        return [
          SearchableActions.InitializePreloadedSearchableItems({ payload: translatedItems }),
        ];
      }),
    ),
  );

  loadDeoedencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.SetSearchableItems),
      switchMap(({ loadedType }) => {
        if (loadedType === 'location') {
          return [
            // SearchableActions.GetSearchableArticles(),
            SearchableActions.GetSearchableEvents(),
            SearchableActions.GetSearchableSavedPeople(),
            SearchableActions.GetSearchableFloors(),
          ];
        }

        if (loadedType === 'event') {
          return [
            SearchableActions.GetSearchableWalls(),
            SearchableActions.GetSearchableAlarms(),
          ];
        }
        return [];
      }),
    ),
  );


  getEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.GetSearchableEvents),
      switchMap(() => {
          return this.alertEventsService.getGlobalSearchEvents(EventModels.PAGE_LIMIT)
            .pipe(
              switchMap((res) => {
                console.log(res);
                return [
                  SearchableActions.FlattenSearchableEvents({ events: res }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),

                ];
              }),
              catchError(err =>
                [SearchableActions.FlattenSearchableFail({ error: err }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ],
              ),
            );
        },
      ),
    ),
  );

  flattenEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.FlattenSearchableEvents),
      withLatestFrom(this.store$.select(SearchableSelectors.selectSearchablesCombinedLookup)),
      concatMap(([{ events }, lookup]) =>
        // this.flattenWorkerService.runFlattenWorker(events, lookup)
        this.dataFlattenerWorkerService.runFlattenWorker({ dataType: 'event', entityLookup: lookup, events }, () => {
            const fallbackResult = flattenEventData(events, lookup);
            console.log('[MAIN-THREAD - events]', fallbackResult);
            return {
              type: 'RUN_FLATTEN_SUCCESS',
              requestId: 'fallback',
              searchableItems: fallbackResult.searchableEvents,
            };
          },
        )
          .pipe(
            // switchMap((result: FlattenEventsWorkerModel.RunEventsFlattenSuccessMessage) => {
            //   return [
            //     SearchableActions.SetSearchableItems({
            //       payload: [
            //         ...result?.searchableEvents,
            //       ],
            //       loadedType: 'event'
            //     })
            //   ]
            // }),
            switchMap((result: DataFlattenerWorkerModel.RunFlattenSuccessMessage) => {
              return [
                SearchableActions.SetSearchableItems({
                  payload: [
                    ...result?.searchableItems,
                  ],
                  loadedType: 'event',
                }),
                // SearchableActions.GetSearchableAlarms()
              ];
            }),
            catchError(err =>
              of(SearchableActions.FlattenSearchableFail({ error: err })),
            ),
          ),
      ),
    ),
  );

  getWalls$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.GetSearchableWalls),
      switchMap(() => {
          // return [
          //   WallV2Actions.getWalls({ query: null })
          // ]
          return this.wallV2Service.getAllByFilters(null, null)
            .pipe(
              switchMap((res) => {
                return [
                  SearchableActions.FlattenSearchableWalls({ walls: res }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ];
              }),
              catchError(err =>
                [
                  SearchableActions.FlattenSearchableFail({ error: err }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ],
              ),
            );
        },
      ),
    ),
  );


  flattenWalls$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.FlattenSearchableWalls),
      // withLatestFrom(this.store$.select(EntityLookupSelectors.selectEntityLookupEntities)),
      withLatestFrom(
        this.store$.select(SearchableSelectors.selectSearchablesCombinedLookup),
        this.store$.select(SearchableSelectors.selectSearchableEvents()),
      ),
      concatMap(([{ walls }, lookup, events]) =>
        // this.flattenWallsWorkerService.runFlattenWorker(walls, lookup)
        this.dataFlattenerWorkerService.runFlattenWorker({
          dataType: 'wall',
          entityLookup: lookup,
          walls,
          events,
        }, () => {
          const fallbackResult = flattenWallData(walls, lookup, events);
          console.log('[MAIN-THREAD - walls]', fallbackResult);
          return {
            type: 'RUN_FLATTEN_SUCCESS',
            requestId: 'fallback',
            searchableItems: fallbackResult.searchableWalls,
          };
        })
          .pipe(
            // switchMap((result: FlattenWallsWorkerModel.RunWallsFlattenSuccessMessage) => {
            //   return [
            //     SearchableActions.SetSearchableItems({
            //       payload: [
            //         ...result?.searchableWalls,
            //       ],
            //       loadedType: 'wall'
            //     })

            //   ]
            // }),
            switchMap((result: DataFlattenerWorkerModel.RunFlattenSuccessMessage) => {
              return [
                SearchableActions.SetSearchableItems({
                  payload: [
                    ...result?.searchableItems,
                  ],
                  loadedType: 'wall',
                }),

              ];
            }),
            catchError(err =>
              of(SearchableActions.FlattenSearchableFail({ error: err })),
            ),
          ),
      ),
    ),
  );

  getAlarms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.GetSearchableAlarms),
      switchMap(() => {
          return this.alarmService.getAlarms()
            .pipe(
              switchMap((res) => {
                return [
                  SearchableActions.FlattenSearchableAlarms({ alarms: res }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ];
              }),
              catchError(err => [
                  SearchableActions.FlattenSearchableFail({ error: err }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ],
              ),
            );
        },
      ),
    ),
  );

  flattenAlarms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.FlattenSearchableAlarms),
      withLatestFrom(this.store$.select(SearchableSelectors.selectSearchableEvents())),
      concatMap(([{ alarms }, events]) =>
        this.dataFlattenerWorkerService.runFlattenWorker({ dataType: 'alarm', alarms, events }, () => {
          const fallbackResult = flattenAlarmData(alarms, events);
          console.log('[MAIN-THREAD - events]', fallbackResult);
          return {
            type: 'RUN_FLATTEN_SUCCESS',
            requestId: 'fallback',
            searchableItems: fallbackResult.searchableAlarms,
          };
        })
          .pipe(
            switchMap((result: DataFlattenerWorkerModel.RunFlattenSuccessMessage) => {
              return [
                SearchableActions.SetSearchableItems({
                  payload: [
                    ...result?.searchableItems,
                  ],
                  loadedType: 'alarm',
                }),
              ];
            }),
            catchError(err =>
              of(SearchableActions.FlattenSearchableFail({ error: err })),
            ),
          ),
      ),
    ),
  );

  getPeople$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.GetSearchableSavedPeople),
      switchMap(() => {
          return this.peopleService.getSavedPeopleWithDetails()
            .pipe(
              switchMap((res) => {
                return [
                  SearchableActions.FlattenearchableSavedPeople({ savedPeople: res }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ];
              }),
              catchError(err =>
                [
                  SearchableActions.FlattenSearchableFail({ error: err }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ],
              ),
            );
        },
      ),
    ),
  );

  flattenSearchablePeople$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.FlattenearchableSavedPeople),
      withLatestFrom(this.store$.select(SearchableSelectors.selectSearchablesCombinedLookup)),
      concatMap(([{ savedPeople }, lookup]) =>
        this.dataFlattenerWorkerService.runFlattenWorker({
          dataType: 'people',
          people: savedPeople,
          entityLookup: lookup,
        }, () => {
          const fallbackResult = flattenPeopleData(savedPeople, lookup);
          console.log('[MAIN-THREAD - events]', fallbackResult);
          return {
            type: 'RUN_FLATTEN_SUCCESS',
            requestId: 'fallback',
            searchableItems: fallbackResult.searchablePeople,
          };
        })
          .pipe(
            switchMap((result: DataFlattenerWorkerModel.RunFlattenSuccessMessage) => {
              const translatedItems = result?.searchableItems?.map(item => {
                if (item.type === 'person') {
                  const appearanceCount = item.appearance || 0;
                  const translatedSubInfo = appearanceCount > 0
                    ? `${appearanceCount} ${appearanceCount === 1 ? this.translateService.instant('SEARCH_GLOBAL_MODAL.APPEARANCE') : this.translateService.instant('SEARCH_GLOBAL_MODAL.APPEARANCES')}`
                    : this.translateService.instant('SEARCH_GLOBAL_MODAL.NO_RECOGNITIONS');
                  return { ...item, subInfo: translatedSubInfo };
                }
                return item;
              }) || [];

              return [
                SearchableActions.SetSearchableItems({
                  payload: translatedItems,
                  loadedType: 'people',
                }),
              ];
            }),
            catchError(err =>
              of(SearchableActions.FlattenSearchableFail({ error: err })),
            ),
          ),
      ),
    ),
  );

  getFloors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.GetSearchableFloors),
      switchMap(() => {
          return this.floorService.getBuildings()
            .pipe(
              switchMap((res) => {
                return [
                  SearchableActions.FlattenearchableFloors({ floors: res }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ];
              }),
              catchError(err =>
                [
                  SearchableActions.FlattenSearchableFail({ error: err }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ],
              ),
            );
        },
      ),
    ),
  );

  flattenSearchableFloors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.FlattenearchableFloors),
      withLatestFrom(this.store$.select(SearchableSelectors.selectSearchablesCombinedLookup)),
      concatMap(([{ floors }, lookup]) =>
        this.dataFlattenerWorkerService.runFlattenWorker({ dataType: 'floor', floors, entityLookup: lookup }, () => {
          const fallbackResult = flattenFloorData(floors, lookup);
          console.log('[MAIN-THREAD - floors]', fallbackResult);
          return {
            type: 'RUN_FLATTEN_SUCCESS',
            requestId: 'fallback',
            searchableItems: [
              ...fallbackResult?.searchableFloors,
              ...fallbackResult?.searchableBuildings,
            ],
            cameraUpdates: fallbackResult.cameraUpdates,
          };
        })
          .pipe(
            switchMap((result: DataFlattenerWorkerModel.RunFlattenSuccessMessage) => {
              const actions: any[] = [
                SearchableActions.SetSearchableItems({
                  payload: [
                    ...result?.searchableItems,
                  ],
                  loadedType: 'floor',
                }),
              ];

              // Update camera entities with floor/building data if available
              if ((result as any).cameraUpdates && (result as any).cameraUpdates.length > 0) {
                actions.push(
                  SearchableActions.UpdateSearchableItems({
                    payload: (result as any).cameraUpdates,
                    loadedType: 'floor',
                  }),
                );
              }

              return actions;
            }),
            catchError(err =>
              of(SearchableActions.FlattenSearchableFail({ error: err })),
            ),
          ),
      ),
    ),
  );

  getArticles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.GetSearchableArticles),
      switchMap(() => {
          return this.articlesService.getArticles()
            .pipe(
              switchMap((res) => {
                return [
                  SearchableActions.FlattenSearchableArticles({ articles: res }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ];
              }),
              catchError(err =>
                [SearchableActions.FlattenSearchableFail({ error: err }),
                  SearchableActions.UpdateSearchableSyncProgress({ progress: SEARCHABLE_SYNC_REQUEST_PROGRESS }),
                ],
              ),
            );
        },
      ),
    ),
  );

  flattenArticles = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchableActions.FlattenSearchableArticles),
      concatMap(({ articles }) =>
        this.dataFlattenerWorkerService.runFlattenWorker({ dataType: 'article', articles }, () => {
            const fallbackResult = flattenArticleData(articles);
            console.log('[MAIN-THREAD - articles]', fallbackResult);
            return {
              type: 'RUN_FLATTEN_SUCCESS',
              requestId: 'fallback',
              searchableItems: fallbackResult.searchableArticles,
            };
          },
        )
          .pipe(
            switchMap((result: DataFlattenerWorkerModel.RunFlattenSuccessMessage) => {
              return [
                SearchableActions.SetSearchableItems({
                  payload: [
                    ...result?.searchableItems,
                  ],
                  loadedType: 'article',
                }),
              ];
            }),
            catchError(err =>
              of(SearchableActions.FlattenSearchableFail({ error: err })),
            ),
          ),
      ),
    ),
  );

  private buildCameraEntities(locations: LocationModel.LocationItem[]): Record<string, LocationModel.LocationCameraItem> {
    const cameraEntities: Record<string, LocationModel.LocationCameraItem> = {};

    if (!Array.isArray(locations) || locations.length === 0) {
      return cameraEntities;
    }

    locations.forEach(location => {
      if (!location?.edges) return;

      Object.values(location.edges).forEach((edge: Edge.EdgeItem) => {
        if (!edge?.cameras) return;

        Object.values(edge.cameras).forEach((camera: LocationModel.LocationCameraItem) => {
          if (camera?.edgeOnly?.cameraId) {
            cameraEntities[camera.edgeOnly.cameraId] = camera;
          }
        });
      });
    });

    return cameraEntities;
  }


  private normalizeHomeLocations(homeLocations: HomeModel.Location[], cameraEntities: Record<string, LocationModel.LocationCameraItem>): HomeModel.Location[] {
    if (!Array.isArray(homeLocations)) {
      return [];
    }

    return homeLocations.map(location => ({
      ...location,
      cameras: (location?.cameras || []).filter((camera: HomeModel.Camera) =>
        camera?.cameraId && cameraEntities[camera.cameraId],
      ),
    }));
  }

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private dataFlattenerWorkerService: DataFlattenerWorkerService,
    private flattenWorkerService: FlattenEventsWorkerService,
    private flattenWallsWorkerService: FlattenWallsWorkerService,
    private alarmService: AlarmService,
    private alertEventsService: AlertEventsService,
    private peopleService: PeopleService,
    private wallV2Service: WallV2Service,
    private floorService: FloorService,
    private translateService: TranslateService,
    private articlesService: ArticlesService,
    private locationService: LocationsService,
    private homeService: HomeService,
  ) {
  }

}
